import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Permission, Role, User } from '@/types'
import { ROLE_PERMISSIONS, hasPermission } from '@/constants/rbac'
import { setAuthToken } from '@/lib/http'
import { buildSeed } from '@/services/mock/seed'
import { USING_API } from '@/services/config'
import { httpClient } from '@/services/http/client'
import { userFromApi } from '@/services/http/mappers'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<User>
  logout: () => void
  can: (permission: Permission) => boolean
  hasRole: (...roles: Role[]) => boolean
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
}

/** Map a backend auth response's user into the frontend User (keep its
 *  server-resolved permissions rather than re-deriving them). */
function authUserFromApi(raw: any): User {
  return { ...userFromApi(raw), permissions: raw.permissions ?? [] }
}

/** Demo accounts — any password works in mock mode. */
function findUser(email: string): User | null {
  const users = buildSeed().users
  return users.find((u) => u.email.toLowerCase() === email.toLowerCase()) ?? users[0]
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      login: async (email, password) => {
        // Real backend: verify email + password and get a signed token.
        if (USING_API) {
          const res = await httpClient.post('/auth/login', { email, password })
          const user = authUserFromApi(res.data.user)
          setAuthToken(res.data.token)
          set({ user, token: res.data.token, isAuthenticated: true })
          return user
        }
        // Mock mode: any password works; look the user up in the demo seed.
        await new Promise((r) => setTimeout(r, 600))
        const user = findUser(email)
        if (!user) throw new Error('No account found for that email.')
        const token = `mock.jwt.${btoa(user.id)}.${Date.now()}`
        setAuthToken(token)
        set({ user, token, isAuthenticated: true })
        return user
      },

      logout: () => {
        setAuthToken(null)
        set({ user: null, token: null, isAuthenticated: false })
      },

      can: (permission) => {
        const user = get().user
        if (!user) return false
        const perms = user.permissions?.length ? user.permissions : ROLE_PERMISSIONS[user.role]
        return hasPermission(perms, permission)
      },

      hasRole: (...roles) => {
        const user = get().user
        return !!user && roles.includes(user.role)
      },

      changePassword: async (currentPassword, newPassword) => {
        if (USING_API) {
          try {
            await httpClient.post('/auth/change-password', {
              currentPassword,
              newPassword,
            })
          } catch (e: any) {
            // Surface the backend's message (e.g. "Current password is
            // incorrect") instead of axios's generic "Request failed…".
            throw new Error(e?.response?.data?.detail ?? 'Could not change password')
          }
          return
        }
        // Mock mode has no real password to check against.
        await new Promise((r) => setTimeout(r, 400))
      },
    }),
    {
      name: 'mcsms.auth',
      partialize: (s) => ({ user: s.user, token: s.token, isAuthenticated: s.isAuthenticated }),
    },
  ),
)
